// src/components/CreatorPage/CreatorPage.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import axios from 'axios';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import API from '../config';
import { AuthContext } from '../../contexts/AuthContext';
import ReviewModal from '../Review/ReviewModal';
import ReportButton from '../Reviews/ReportButton';
import {
  Container,
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Avatar,
  Button,
  Grid,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Chip,
  Rating,
  useTheme,
  alpha,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fade,
  Slide,
  Alert,
  AlertTitle,
} from '@mui/material';
import { 
  ThumbUp, 
  ThumbDown, 
  OpenInNew, 
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  FilterList as FilterIcon,
  Schedule as ScheduleIcon,
  Psychology as PsychologyIcon,
  Favorite as FavoriteIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  RateReview as RateReviewIcon,
} from '@mui/icons-material';

const spentMap = {
  '$0–25':    12.5,
  '$25–50':   37.5,
  '$50–100':  75,
  '$100–250': 175,
  '$250+':    250,
};

export default function CreatorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { authData } = useContext(AuthContext);
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isVIP, setIsVIP] = useState(false);
  const [showVerificationInfo, setShowVerificationInfo] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    recommend: '',
    spent: '',
    contentQuality: 0,
    contentFrequency: 0,
    authenticity: 0,
    consistency: 0,
    engagement: 0,
    fullReview: '',
  });
  
  // Filter and sort state
  const [verifiedFilter, setVerifiedFilter] = useState('all'); // 'all', 'verified', 'unverified'
  const [recommendFilter, setRecommendFilter] = useState('all'); // 'all', 'yes', 'no'
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'highest', 'lowest'

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch creator data
        const { data } = await axios.get(`${API}/api/creators/${id}`);
        setCreator(data.creator);
        
        // Fetch current user ID and VIP status if logged in
        if (authData?.access_token) {
          try {
            const userResponse = await axios.get(`${API}/api/dashboard`, {
              headers: { Authorization: `Bearer ${authData.access_token}` }
            });
            setCurrentUserId(userResponse.data.profile._id);
            const subscriptionStatus = userResponse.data.profile?.subscriptionStatus;
            setIsVIP(['active', 'cancel_at_period_end', 'trialing', 'past_due', 'promo_active'].includes(subscriptionStatus));
          } catch (err) {
            console.error('Failed to fetch user data:', err);
          }
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load creator details.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, authData]);

  // Check if current user has reviewed this creator
  const userHasReviewed = useMemo(() => {
    if (!currentUserId || !creator?.reviews) return false;
    return creator.reviews.some(review => review.user_id === currentUserId);
  }, [currentUserId, creator]);

  // Filter and sort reviews
  const filteredReviews = useMemo(() => {
    if (!creator || !creator.reviews) return [];
    
    let filtered = [...creator.reviews];
    
    // Apply verified filter
    if (verifiedFilter === 'verified') {
      filtered = filtered.filter(r => r.verified);
    } else if (verifiedFilter === 'unverified') {
      filtered = filtered.filter(r => !r.verified);
    }
    
    // Apply recommend filter
    if (recommendFilter === 'yes') {
      filtered = filtered.filter(r => r.recommend);
    } else if (recommendFilter === 'no') {
      filtered = filtered.filter(r => !r.recommend);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'highest':
          return b.contentQuality - a.contentQuality;
        case 'lowest':
          return a.contentQuality - b.contentQuality;
        default:
          return 0;
      }
    });
    
    // If not VIP, only show user's own review
    if (!isVIP && currentUserId) {
      filtered = filtered.filter(r => r.user_id === currentUserId);
    }
    
    return filtered;
  }, [creator, verifiedFilter, recommendFilter, sortBy, isVIP, currentUserId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh' }}>
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 500 }}>Loading creator...</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: alpha(theme.palette.error.main, 0.1) }}>
          <Typography color="error" variant="h6">{error}</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', pb: 8, mt: { xs: -7, sm: -8, md: -9 } }}>
      {/* Banner & Avatar */}
      <Box sx={{ position: 'relative', mb: 6, mt: 0 }}>
        <Box
          sx={{
            height: { xs: 250, sm: 320 },
            backgroundImage: `url(${creator.bannerImage || '/default-banner.jpg'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '40%',
              background: `linear-gradient(to top, ${theme.palette.background.default}, transparent)`,
            }}
          />
        </Box>
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', top: -60, textAlign: 'center' }}>
            <Avatar
              src={creator.profilePicture || '/default-profile.png'}
              alt={creator.username}
              sx={{
                width: { xs: 120, sm: 140 },
                height: { xs: 120, sm: 140 },
                border: `6px solid ${theme.palette.background.paper}`,
                boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.4)}`,
                mx: 'auto',
                display: 'block',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -4, pt: 0 }}>
        {/* Profile Details */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.75rem', sm: '2rem' },
              mb: 1.5,
            }}
          >
            {creator.username}
          </Typography>
          {creator.bio && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mb: 2, maxWidth: 500, mx: 'auto' }}
            >
              {creator.bio}
            </Typography>
          )}
          <Link href={creator.profile_url} target="_blank" rel="noopener" underline="none">
            <Button 
              variant="contained" 
              endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
              size="small"
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                px: 3,
                py: 1,
                borderRadius: 2,
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                '&:hover': {
                  boxShadow: `0 6px 28px ${alpha(theme.palette.primary.main, 0.5)}`,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              View OnlyFans Profile
            </Button>
          </Link>
        </Box>

        {/* Enhanced Stats Cards - Centered and Square */}
        <Fade in timeout={800}>
          <Box sx={{ mb: 6, maxWidth: { xs: '100%', sm: '95%', md: '85%', lg: '70%' }, mx: 'auto' }}>
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} justifyContent="center" wrap="nowrap">
              {/* Average Rating */}
              <Grid item xs={4} sm={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    textAlign: 'center',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    borderRadius: { xs: 2, sm: 3 },
                    aspectRatio: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.5)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: 40, sm: 50, md: 60 },
                      height: { xs: 40, sm: 50, md: 60 },
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.common.white, 0.2),
                      mb: { xs: 1, sm: 1.5, md: 2 },
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <StarIcon sx={{ fontSize: { xs: 20, sm: 28, md: 36 }, color: 'white' }} />
                  </Box>
                  <Typography variant="h2" fontWeight={900} color="white" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem' }, lineHeight: 1, mb: { xs: 0.5, sm: 1 } }}>
                    {creator.averageRating > 0 ? creator.averageRating.toFixed(1) : 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.9), fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.85rem', md: '1rem' } }}>
                    Average Quality
                  </Typography>
                  {creator.averageRating > 0 && (
                    <Rating 
                      value={creator.averageRating} 
                      precision={0.1} 
                      readOnly 
                      size="small"
                      sx={{
                        mt: 1,
                        '& .MuiRating-icon': { fontSize: { xs: '0.9rem', sm: '1.1rem' } },
                        '& .MuiRating-iconFilled': {
                          color: 'white',
                        },
                        '& .MuiRating-iconEmpty': {
                          color: alpha(theme.palette.common.white, 0.3),
                        },
                      }}
                    />
                  )}
                  <Typography variant="caption" sx={{ mt: { xs: 0.5, sm: 1 }, color: alpha(theme.palette.common.white, 0.8), fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                    {creator.reviewCount} review{creator.reviewCount !== 1 ? 's' : ''}
                  </Typography>
                </Paper>
              </Grid>

              {/* Recommend Percentage */}
              <Grid item xs={4} sm={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    textAlign: 'center',
                    background: creator.recPct >= 75
                      ? `linear-gradient(135deg, #4CAF50, #388E3C)`  // Green for 75%+
                      : creator.recPct >= 50
                      ? `linear-gradient(135deg, #FFC107, #F9A825)`  // Yellow for 50-75%
                      : `linear-gradient(135deg, #EF5350, #D32F2F)`, // Red for <50%
                    borderRadius: { xs: 2, sm: 3 },
                    aspectRatio: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: creator.recPct >= 75
                      ? `0 8px 32px ${alpha('#4CAF50', 0.4)}`
                      : creator.recPct >= 50
                      ? `0 8px 32px ${alpha('#FFC107', 0.4)}`
                      : `0 8px 32px ${alpha('#EF5350', 0.4)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: creator.recPct >= 75
                        ? `0 16px 48px ${alpha('#4CAF50', 0.5)}`
                        : creator.recPct >= 50
                        ? `0 16px 48px ${alpha('#FFC107', 0.5)}`
                        : `0 16px 48px ${alpha('#EF5350', 0.5)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: 40, sm: 50, md: 60 },
                      height: { xs: 40, sm: 50, md: 60 },
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.common.white, 0.2),
                      mb: { xs: 1, sm: 1.5, md: 2 },
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    {creator.recPct >= 50 ? (
                      <ThumbUp sx={{ fontSize: { xs: 20, sm: 28, md: 36 }, color: 'white' }} />
                    ) : (
                      <ThumbDown sx={{ fontSize: { xs: 20, sm: 28, md: 36 }, color: 'white' }} />
                    )}
                  </Box>
                  <Typography variant="h2" fontWeight={900} color="white" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem' }, lineHeight: 1, mb: { xs: 0.5, sm: 1 } }}>
                    {creator.recPct > 0 ? `${Math.round(creator.recPct)}%` : 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.9), fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.95rem' } }}>
                    Recommend
                  </Typography>
                  {/* Spacer to match rating height */}
                  <Box sx={{ height: { xs: 20, sm: 24 } }} />
                  <Typography variant="caption" sx={{ mt: { xs: 0.5, sm: 1 }, color: alpha(theme.palette.common.white, 0.8), fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                    {creator.reviewCount} review{creator.reviewCount !== 1 ? 's' : ''}
                  </Typography>
                </Paper>
              </Grid>

              {/* Average Spent */}
              <Grid item xs={4} sm={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    textAlign: 'center',
                    background: `linear-gradient(135deg, #7C9BF5, #5E7CE2)`,
                    borderRadius: { xs: 2, sm: 3 },
                    aspectRatio: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.5)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: 40, sm: 50, md: 60 },
                      height: { xs: 40, sm: 50, md: 60 },
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.common.white, 0.2),
                      mb: { xs: 1, sm: 1.5, md: 2 },
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <MoneyIcon sx={{ fontSize: { xs: 20, sm: 28, md: 36 }, color: 'white' }} />
                  </Box>
                  <Typography variant="h2" fontWeight={900} color="white" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem' }, lineHeight: 1, mb: { xs: 0.5, sm: 1 } }}>
                    {creator.avgSpent > 0 ? `$${Math.round(creator.avgSpent)}` : 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.9), fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.85rem', md: '1rem' } }}>
                    Average Spent
                  </Typography>
                  {/* Spacer to match rating height */}
                  <Box sx={{ height: { xs: 20, sm: 24 } }} />
                  <Typography variant="caption" sx={{ mt: { xs: 0.5, sm: 1 }, color: alpha(theme.palette.common.white, 0.8), fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                    Per subscriber
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Fade>

        {/* Reviews Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h4" fontWeight={700}>
              Reviews ({creator.reviews?.length || creator.reviewCount || 0})
            </Typography>
            {authData && !userHasReviewed && (
              <Button
                variant="contained"
                startIcon={<RateReviewIcon />}
                onClick={() => setShowReviewModal(true)}
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
                }}
              >
                Add My Review
              </Button>
            )}
          </Box>

          {/* Verification Info Banner */}
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{
              mb: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.05),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>About Review Verification</AlertTitle>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Verified reviews</strong> are from users who have connected their OnlyFans account and are confirmed subscribers of this creator.
              <strong> Unverified reviews</strong> are from users who haven't linked their account.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/dashboard')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: theme.palette.info.main,
                borderColor: theme.palette.info.main,
                mt: 1,
                '&:hover': {
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  borderColor: theme.palette.info.dark,
                },
              }}
            >
              Learn how to verify your account →
            </Button>
          </Alert>

          {/* Filters and Sort */}
          {creator.reviews.length > 0 && (
            <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Grid container spacing={2} alignItems="center">
                {/* Verified Filter */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Verification</InputLabel>
                    <Select
                      value={verifiedFilter}
                      label="Verification"
                      onChange={(e) => setVerifiedFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Reviews</MenuItem>
                      <MenuItem value="verified">Verified Only</MenuItem>
                      <MenuItem value="unverified">Unverified Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Recommend Filter */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Recommendation</InputLabel>
                    <Select
                      value={recommendFilter}
                      label="Recommendation"
                      onChange={(e) => setRecommendFilter(e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="yes">Recommended</MenuItem>
                      <MenuItem value="no">Not Recommended</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Sort */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={sortBy}
                      label="Sort By"
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <MenuItem value="newest">Newest First</MenuItem>
                      <MenuItem value="oldest">Oldest First</MenuItem>
                      <MenuItem value="highest">Highest Rated</MenuItem>
                      <MenuItem value="lowest">Lowest Rated</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Reviews Grid */}
          {creator.reviewCount === 0 ? (
            // No reviews at all - encourage user to be first
            <Paper 
              elevation={0}
              sx={{ 
                p: 6, 
                textAlign: 'center', 
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 4,
              }}
            >
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: theme.palette.primary.main }}>
                🌟 Be the First to Review!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                This creator doesn't have any reviews yet. Share your experience and help the community make informed decisions!
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => setShowReviewModal(true)}
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.5)}`,
                  },
                }}
              >
                Write the First Review
              </Button>
            </Paper>
          ) : !isVIP && !userHasReviewed && creator.reviewCount > 0 ? (
            // Non-VIP user hasn't reviewed but reviews exist - show VIP marketing banner
            <Paper
              elevation={0}
              sx={{
                p: 6,
                borderRadius: 4,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                color: 'white',
                textAlign: 'center',
                border: `3px solid ${alpha(theme.palette.common.white, 0.2)}`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 150,
                  height: 150,
                  borderRadius: '50%',
                  background: alpha(theme.palette.common.white, 0.1),
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -30,
                  left: -30,
                  width: 180,
                  height: 180,
                  borderRadius: '50%',
                  background: alpha(theme.palette.common.white, 0.05),
                }}
              />
              <Typography variant="h4" fontWeight={800} sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
                🔒 {creator.reviewCount} Review{creator.reviewCount !== 1 ? 's' : ''} Available
              </Typography>
              <Typography variant="h6" sx={{ mb: 3, maxWidth: 650, mx: 'auto', position: 'relative', zIndex: 1, opacity: 0.95, fontWeight: 500 }}>
                This creator has <strong>{creator.reviewCount} community review{creator.reviewCount !== 1 ? 's' : ''}</strong> with valuable insights about content quality, engagement, and value for money.
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, maxWidth: 700, mx: 'auto', position: 'relative', zIndex: 1, opacity: 0.9 }}>
                Want to see what the community is saying? Upgrade to VIP to unlock all reviews, access verified subscriber insights, and make informed decisions about creators!
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/subscription')}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  borderRadius: 3,
                  textTransform: 'none',
                  position: 'relative',
                  zIndex: 1,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.2)}`,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.95),
                    transform: 'translateY(-3px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.3)}`,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Unlock VIP Access
              </Button>
            </Paper>
          ) : filteredReviews.length > 0 ? (
            <Stack spacing={3}>
              {filteredReviews.map((review, idx) => (
                <Fade in timeout={600 + (idx * 100)} key={idx}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: 4,
                      background: theme.palette.background.paper,
                      border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateX(8px)',
                        boxShadow: `0 8px 40px ${alpha(theme.palette.common.black, 0.12)}`,
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {/* Header Row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        src={review.user_info?.profilePicture || '/default-profile.png'}
                        alt={review.user_info?.username || 'Anonymous'}
                        sx={{ 
                          width: 48, 
                          height: 48,
                        }}
                      />
                      
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" fontWeight={600} noWrap>
                            {review.user_info?.username || 'Anonymous'}
                          </Typography>
                          {review.user_id === currentUserId && (
                            <Chip
                              label="My Review"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                color: 'white',
                              }}
                            />
                          )}
                          {review.verified ? (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                              label="VERIFIED REVIEW"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                                color: 'white',
                                '& .MuiChip-icon': { color: 'white' },
                              }}
                            />
                          ) : (
                            <Chip
                              label="Unverified"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                bgcolor: alpha(theme.palette.grey[400], 0.2),
                                color: theme.palette.text.secondary,
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {review.createdAt && !isNaN(new Date(review.createdAt).getTime())
                            ? new Date(review.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'Date unavailable'}
                        </Typography>
                      </Box>
                      
                      {/* Rating & Recommend */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <StarIcon sx={{ fontSize: 20, color: theme.palette.warning.main }} />
                          <Typography variant="h6" fontWeight={700}>
                            {review.contentQuality}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: review.recommend 
                              ? alpha(theme.palette.success.main, 0.15)
                              : alpha(theme.palette.error.main, 0.15),
                          }}
                        >
                          {review.recommend ? (
                            <ThumbUp sx={{ fontSize: 18, color: theme.palette.success.main }} />
                          ) : (
                            <ThumbDown sx={{ fontSize: 18, color: theme.palette.error.main }} />
                          )}
                        </Box>
                        <ReportButton reviewId={review._id} isOwnReview={review.user_id === currentUserId} />
                      </Box>
                    </Box>
                    
                    {/* Review Text */}
                    {review.fullReview && (
                      <Box sx={{ my: 2, py: 2, px: 0 }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontStyle: 'italic',
                            color: 'text.primary',
                            lineHeight: 1.6,
                            fontSize: '0.95rem',
                          }}
                        >
                          "{review.fullReview}"
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Metrics - Two Rows */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                      {/* First Row: Spent */}
                      <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 3 }, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                            }}
                          >
                            <MoneyIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1, fontSize: '0.7rem' }}>
                              Spent
                            </Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                              {review.spent || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Second Row: Frequency, Authenticity, Engagement */}
                      <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 3 }, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette.info.main, 0.1),
                            }}
                          >
                            <ScheduleIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1, fontSize: '0.7rem' }}>
                              Frequency
                            </Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                              {review.contentFrequency}/5
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette.warning.main, 0.1),
                            }}
                          >
                            <PsychologyIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1, fontSize: '0.7rem' }}>
                              Authenticity
                            </Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                              {review.authenticity}/5
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                            }}
                          >
                            <FavoriteIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1, fontSize: '0.7rem' }}>
                              Engagement
                            </Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                              {review.engagement}/5
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Fade>
              ))}
              
              {/* VIP Marketing Banner for non-VIP users */}
              {!isVIP && userHasReviewed && creator.reviews.length > 1 && (
                <Fade in timeout={1000}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 5,
                      borderRadius: 4,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      color: 'white',
                      textAlign: 'center',
                      border: `3px solid ${alpha(theme.palette.common.white, 0.2)}`,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        background: alpha(theme.palette.common.white, 0.1),
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: -30,
                        left: -30,
                        width: 180,
                        height: 180,
                        borderRadius: '50%',
                        background: alpha(theme.palette.common.white, 0.05),
                      }}
                    />
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
                      🔒 Curious What Others Think?
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, maxWidth: 700, mx: 'auto', position: 'relative', zIndex: 1, opacity: 0.95 }}>
                      This creator has <strong>{creator.reviews.length - 1} other review{creator.reviews.length - 1 !== 1 ? 's' : ''}</strong> from the community. 
                      Upgrade to VIP to unlock all reviews, see verified subscriber insights, and discover what others are saying about creators you're interested in!
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/subscription')}
                      sx={{
                        bgcolor: 'white',
                        color: theme.palette.primary.main,
                        px: 5,
                        py: 1.5,
                        fontWeight: 700,
                        fontSize: '1.05rem',
                        borderRadius: 3,
                        textTransform: 'none',
                        position: 'relative',
                        zIndex: 1,
                        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.2)}`,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.common.white, 0.95),
                          transform: 'translateY(-3px)',
                          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.3)}`,
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Unlock VIP Access
                    </Button>
                  </Paper>
                </Fade>
              )}
            </Stack>
          ) : (
            <Paper sx={{ p: 8, textAlign: 'center', bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
              <Typography variant="h6" color="text.secondary">
                No reviews match your filters.
              </Typography>
            </Paper>
          )}
        </Box>
      </Container>

      {/* Review Modal */}
      {creator && (
        <ReviewModal
          open={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
            setReviewForm({
              recommend: '',
              spent: '',
              contentQuality: 0,
              contentFrequency: 0,
              authenticity: 0,
              consistency: 0,
              engagement: 0,
              fullReview: '',
            });
          }}
          reviewCreator={{
            id: creator.id,
            username: creator.username,
            profilePicture: creator.profilePicture,
          }}
          reviewForm={reviewForm}
          setReviewForm={setReviewForm}
          onSubmit={async () => {
            try {
              const payload = {
                creator_id: creator.id,
                creator_onlyfans_id: creator.user_id,
                recommend: reviewForm.recommend,
                spent: reviewForm.spent,
                contentQuality: reviewForm.contentQuality,
                contentFrequency: reviewForm.contentFrequency,
                authenticity: reviewForm.authenticity,
                consistency: reviewForm.consistency,
                engagement: reviewForm.engagement,
                fullReview: reviewForm.fullReview,
              };

              await axios.post(
                `${API}/api/reviews`,
                payload,
                { headers: { Authorization: `Bearer ${authData.access_token}` } }
              );

              setShowReviewModal(false);
              // Refresh the page to show the new review
              window.location.reload();
            } catch (error) {
              console.error('Failed to submit review:', error);
              alert('Failed to submit review. Please try again.');
            }
          }}
        />
      )}
    </Box>
  );
}
